  // Supabase Functions base URL (from env)
  const baseFunctionsUrl = `${process.env.EXPO_PUBLIC_SUPABASE_URL}/functions/v1`;
// Helper to get a fresh Supabase access token (verbose debugging)
async function getFreshToken() {
  console.log("🔐 [getFreshToken] Starting token refresh sequence...");
  try {
    const { data, error } = await supabase.auth.refreshSession();
    console.log("🔐 [getFreshToken] Supabase.refreshSession() result:", {
      error,
      sessionExists: !!data?.session,
      userId: data?.session?.user?.id,
      expiresAt: data?.session?.expires_at,
    });

    if (error) {
      console.error("❌ [getFreshToken] Supabase refreshSession error:", error);
      throw error;
    }

    const token = data.session?.access_token;
    const refreshToken = data.session?.refresh_token;

    if (!token) {
      console.warn("⚠️ [getFreshToken] No access_token found after refresh. Data:", data);
      throw new Error("No access token found after refresh");
    }

    console.log("✅ [getFreshToken] Token successfully refreshed:");
    console.log({
      tokenPrefix: token.slice(0, 15),
      refreshTokenPrefix: refreshToken?.slice(0, 10),
      expiresAt: data.session?.expires_at,
    });

    return token;
  } catch (err) {
    console.error("💥 [getFreshToken] Fatal error while refreshing session:", err);
    if (err instanceof Error) {
      throw new Error(`[getFreshToken] Invalid or expired session: ${err.message}`);
    } else {
      throw new Error("Invalid or expired session (unknown error)");
    }
  }
}
import { useState, useEffect, useRef } from "react";
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  Platform,
} from "react-native";
import { platformAlert as showAlert } from "../utils/platformAlert";
import { Image } from "expo-image";
import {
  useStripe,
  isPlatformPaySupported,
  confirmPayment,
} from "../hooks/useStripe";
import { useNavigation, useRoute, RouteProp } from "@react-navigation/native";
import { Feather } from "@expo/vector-icons";
import { spacing } from "../theme";
import { useColors, ThemeColors } from "../hooks/useColors";
import { supabase } from "../lib/supabase";
import { RootStackParamList } from "../navigation/types";
import { NativeStackNavigationProp } from "@react-navigation/native-stack";
import { DeliveryAddress } from "../types/delivery";
import { SafeAreaView } from "react-native-safe-area-context";
import { useDeliveryAddressContext } from "../contexts/DeliveryAddressContext";
import GeocodingService from "../services/GeocodingService";
import { DELIVERY_CONFIG, DeliveryHelpers } from "../config/deliveryConfig";
import { useAuth } from "../hooks/useAuth";
import { useQuery } from "@tanstack/react-query";
import { usePaymentMethods } from "../hooks/usePaymentMethods";
import { useCart } from "../hooks/useCart";
import { useProducts } from "../hooks/useProducts";
import { useOrders } from "../hooks/useOrders";
import {
  forceOrderBannerRefresh,
  forceProductListRefresh,
} from "../utils/orderEvents";
import { uberDirectService } from "../services/UberDirectService";
import { useNotifications } from "../hooks/useNotifications";
import { usePostHog } from "../hooks/usePostHog";
import { trackEvent } from "../services/posthog";
import { formatPrice, getWebImageUrl } from "../utils/format";
import { 
  isStoreCurrentlyOpen, 
  validateDeliveryTimeAgainstStoreHours,
  type StoreHours 
} from "../utils/storeHours";
import { useVendorHoursRealtime } from "../hooks/useVendorHoursRealtime";
import { useIsFocused, useFocusEffect } from "@react-navigation/native";
import { useCallback, useMemo } from "react";

// Configuration constants moved to deliveryConfig.ts

// Distance calculation function moved to deliveryConfig.ts

type CheckoutScreenNavigationProp =
  NativeStackNavigationProp<RootStackParamList>;

interface CartItem {
  id: string;
  quantity: number;
  product: {
    id: string;
    title: string;
    price: number;
    images: string[];
    size?: string;
    quantity: number;
    vendor_id: string;
    vendor: {
      id: string;
      store_name: string;
      street_line1: string;
      city: string;
      state: string;
      zip_code: string;
      country: string;
      stripe_accountid?: string;
      latitude?: number;
      longitude?: number;
      delivery_radius?: string;
      phone_number?: string;
    };
  };
}

interface CheckoutScreenParams {
  total: number;
  cartItems: CartItem[];
  deliveryFee: number;
  deliveryQuote: any[];
  deliveryAddress: DeliveryAddress;
  vendorAvailability?: {
    vendorId: string;
    isAvailable: boolean;
    responseTime: number;
    lastUpdate: string;
  }[];
}

export default function CheckoutScreen() {
  const colors = useColors();
  const styles = createStyles(colors);
  const { initPaymentSheet, presentPaymentSheet } = useStripe();
  const navigation = useNavigation<CheckoutScreenNavigationProp>();
  const route =
    useRoute<RouteProp<{ params: CheckoutScreenParams }, "params">>();
  const { cartItems: routeCartItems, deliveryFee, deliveryQuote, deliveryAddress } =
    route.params;
  const { selectedAddress } = useDeliveryAddressContext();
  const { session, user } = useAuth();
  const { cartItems: liveCartItems, refreshCart, isLoading: isCartLoading } = useCart();
  const posthog = usePostHog();
  
  // Use live cart items from hook if available (even if empty), otherwise fall back to route params
  const cartItems = (liveCartItems === undefined || isCartLoading) ? routeCartItems : liveCartItems;
  
  const { refetch: refetchProducts } = useProducts();
  const { refetch: refetchOrders } = useOrders();
  const { sendOrderUpdate } = useNotifications();

  const [loading, setLoading] = useState(false);
  
  // Test mode for payment amount (only use for actual payment testing)
  const [isTestMode, setIsTestMode] = useState(false);
  // Robo courier for sandbox testing only - disable for production
  const useRoboCourier = false; // Disabled for production deliveries
  const [platformPaySupported, setPlatformPaySupported] = useState<
    boolean | null
  >(null);
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState<
    string | null
  >(null);
  const [useNewCard, setUseNewCard] = useState(false);
  const [isCreatingDelivery, setIsCreatingDelivery] = useState(false);
  
  // Success modal state - used instead of native Alert for payment success
  // This prevents iOS Alert issues where the underlying screen becomes visible
  
  // Store channel refs so we can unsubscribe during payment
  const cartDeletionChannelRef = useRef<any>(null);
  const productInventoryChannelRef = useRef<any>(null);
  
  // Track previous cart count to detect when cart becomes empty unexpectedly
  const prevCartCountRef = useRef<number>(cartItems?.length ?? 0);
  const isProcessingPaymentRef = useRef<boolean>(false);
  // Track if payment succeeded - used to keep loading=true until navigation
  const paymentSucceededRef = useRef<boolean>(false);
  // Freeze display totals when payment succeeds to prevent UI from showing wrong values
  const frozenTotalsRef = useRef<{
    merchandiseSubtotal: number;
    deliveryTotal: number;
    buyerFee: number;
    payableTotal: number;
  } | null>(null);

  console.log("VENDOR", cartItems[0]?.product?.vendor);

  const isFocused = useIsFocused();

  // State to track if store hours changed and block checkout
  const [storeHoursBlocked, setStoreHoursBlocked] = useState(false);
  const [storeHoursBlockedReason, setStoreHoursBlockedReason] = useState<string | null>(null);

  // Get unique vendor IDs from cart items for realtime monitoring
  const cartVendorIds = useMemo(() => {
    const items = cartItems || [];
    const ids = new Set(items.map((item) => item.product.vendor_id));
    return Array.from(ids);
  }, [cartItems]);

  // Subscribe to real-time vendor delivery_hours changes
  // When a vendor updates their store hours during checkout, we need to block payment
  const { isConnected: hoursRealtimeConnected } = useVendorHoursRealtime({
    vendorIds: cartVendorIds,
    enabled: cartVendorIds.length > 0 && isFocused && !loading,
    onHoursChange: useCallback((update) => {
      // Don't show store hours alert if payment already succeeded
      if (paymentSucceededRef.current) {
        console.log('🕒 [CheckoutScreen] Ignoring hours change - payment already completed');
        return;
      }
      
      console.log(`🕒 [CheckoutScreen] Vendor hours changed in real-time:`, {
        vendorId: update.vendorId,
        vendorName: update.vendorName,
        newHours: update.deliveryHours,
      });
      
      // Block checkout until user acknowledges and re-validates
      setStoreHoursBlocked(true);
      setStoreHoursBlockedReason(`${update.vendorName || 'The store'} has updated their hours.`);
      
      // Show alert to user
      showAlert(
        "Store Hours Changed",
        `${update.vendorName || 'The store'} has updated their hours while you were checking out. Please go back to your cart to verify the store is still open.`,
        [
          {
            text: "Go Back to Cart",
            onPress: () => {
              navigation.goBack();
            },
          },
        ]
      );
    }, [navigation]),
  });

  // Log realtime connection status for debugging
  useEffect(() => {
    if (cartVendorIds.length > 0) {
      console.log(`🔌 [CheckoutScreen] Vendor hours realtime:`, {
        connected: hoursRealtimeConnected,
        monitoringVendors: cartVendorIds.length,
        blocked: storeHoursBlocked,
      });
    }
  }, [hoursRealtimeConnected, cartVendorIds.length, storeHoursBlocked]);

  const { data: profile } = useQuery({
    queryKey: ["profile", session?.user?.id],
    queryFn: async () => {
      if (!session?.user?.id) return null;
      const { data } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", session.user.id)
        .single();
      return data;
    },
    enabled: !!session?.user?.id,
  });

  console.log("cartItemscartItemscartItemscartItems:", cartItems);

  // Use ref to track current cartItems for realtime callbacks
  const cartItemsRef = useRef(cartItems);
  useEffect(() => {
    cartItemsRef.current = cartItems;
  }, [cartItems]);
  
  // Also update ref when live cart items change to ensure real-time updates
  useEffect(() => {
    if (liveCartItems && liveCartItems.length > 0) {
      cartItemsRef.current = liveCartItems;
    }
  }, [liveCartItems]);

  // Detect when cart becomes empty unexpectedly (another user bought the items)
  // This handles the case where the realtime listeners don't fire in time
  useEffect(() => {
    const currentCount = cartItems?.length ?? 0;
    const prevCount = prevCartCountRef.current;
    
    // Skip if cart is still loading
    if (isCartLoading) return;
    
    // Skip if we're processing a payment (user emptied their own cart)
    if (isProcessingPaymentRef.current) {
      prevCartCountRef.current = currentCount;
      return;
    }
    
    // Detect transition from non-empty to empty
    if (prevCount > 0 && currentCount === 0) {
      // Don't show alert if payment already succeeded (we cleared the cart ourselves)
      if (paymentSucceededRef.current) {
        prevCartCountRef.current = currentCount;
        return;
      }
      
      console.log('[CheckoutScreen] Cart became empty unexpectedly, navigating back');
      // On web, navigate immediately before showing alert
      if (Platform.OS === 'web') {
        navigation.reset({
          index: 0,
          routes: [{ name: 'Main' }],
        });
      }
      showAlert(
        "Cart Empty",
        "Items in your cart are no longer available.",
        [
          {
            text: "Okay",
            onPress: () => {
              if (Platform.OS !== 'web') {
                navigation.popToTop();
              }
            }
          }
        ]
      );
    }
    
    // Update ref
    prevCartCountRef.current = currentCount;
  }, [cartItems?.length, isCartLoading, navigation]);

  // Listen for cart_items deletions (when items are removed by other devices/checkout)
  // This handles the case where another user buys the item you have in your cart
  useEffect(() => {
    if (!session?.user?.id) return;

    console.log("[CheckoutScreen] Setting up realtime listener for cart deletions");
    const channel = supabase
      .channel(`checkout-cart-deletions-${session.user.id}`)
      .on(
        'postgres_changes',
        {
          event: 'DELETE',
          schema: 'public',
          table: 'cart_items',
          filter: `user_id=eq.${session.user.id}`,
        },
        async (payload) => {
          // Don't navigate if payment is in progress or already succeeded
          if (isProcessingPaymentRef.current || paymentSucceededRef.current) {
            return;
          }
          
          // Check if the deleted item was in our current cart using ref
          const deletedProductId = payload.old?.product_id;
          const currentCartItems = cartItemsRef.current;
          const isInCurrentCart = currentCartItems.some(item => item.product.id === deletedProductId);
          
          if (isInCurrentCart) {
            // Refresh cart first to get latest state
            await refreshCart();
            
            // On web, navigate immediately before showing alert
            if (Platform.OS === 'web') {
              navigation.reset({
                index: 0,
                routes: [{ name: 'Main' }],
              });
            }
            
            // Show alert before navigation
            showAlert(
              "Cart Updated",
              "An item in your cart is no longer available and has been removed.",
              [
                {
                  text: "Okay",
                  onPress: () => {
                    if (Platform.OS !== 'web') {
                      navigation.popToTop();
                    }
                  }
                }
              ]
            );
          }
        }
      )
      .subscribe((status) => {
        console.log('[CheckoutScreen] Realtime subscription status:', status);
      });

    // Store channel ref so we can unsubscribe during payment
    cartDeletionChannelRef.current = channel;

    return () => {
      console.log("[CheckoutScreen] Cleaning up realtime listener");
      supabase.removeChannel(channel);
      cartDeletionChannelRef.current = null;
    };
  }, [session?.user?.id, refreshCart, navigation]);

  // Listen for product inventory updates (quantity/status changes)
  // This handles the case where another user buys the item, making it unavailable
  useEffect(() => {
    if (!session?.user?.id || !cartItems || cartItems.length === 0) return;

    console.log("[CheckoutScreen] Setting up realtime listener for product inventory updates");
    
    // Get product IDs from current cart items for filtering in callback
    const productIds = new Set(cartItems.map(item => item?.product?.id).filter(Boolean));
    if (productIds.size === 0) return;

    const channel = supabase
      .channel(`checkout-product-inventory-${session.user.id}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'products',
        },
        async (payload) => {
          // Don't navigate if payment is in progress or already succeeded
          if (isProcessingPaymentRef.current || paymentSucceededRef.current) {
            return;
          }
          
          const newRow: any = payload.new;
          const oldRow: any = payload.old;
          const productId = newRow?.id;
          
          // Only process updates for products in our current cart
          if (!productIds.has(productId)) {
            return;
          }
          
          const quantityChanged = Number(newRow?.quantity ?? NaN) !== Number(oldRow?.quantity ?? NaN);
          const statusChanged = newRow?.status !== oldRow?.status;
          
          if (!quantityChanged && !statusChanged) {
            return;
          }

          // Use ref to get current cartItems to avoid stale closure
          const currentCartItems = cartItemsRef.current;
          const cartItem = currentCartItems.find(item => item.product.id === productId);
          if (!cartItem) return;

          const newQuantity = Number(newRow?.quantity ?? 0);
          const newStatus = newRow?.status;
          const requestedQuantity = cartItem.quantity;

          // Check if product is now completely unavailable (sold out, archived, or quantity is 0)
          const isCompletelyUnavailable = 
            newStatus === 'sold' || 
            newStatus === 'archived' || 
            newQuantity <= 0;

          if (isCompletelyUnavailable) {
            console.log('[CheckoutScreen] Product in cart became unavailable, removing from cart and navigating to Home:', {
              productId,
              productTitle: cartItem.product.title,
              newQuantity,
              newStatus,
              requestedQuantity,
            });

            // Remove the item from cart
            try {
              const { error: deleteError } = await supabase
                .from('cart_items')
                .delete()
                .eq('id', cartItem.id);

              if (deleteError) {
                console.error('[CheckoutScreen] Failed to remove out-of-stock item from cart:', deleteError);
              } else {
                console.log('[CheckoutScreen] Successfully removed out-of-stock item from cart');
              }
            } catch (error) {
              console.error('[CheckoutScreen] Error removing item from cart:', error);
            }

            // Refresh cart first to get latest state
            await refreshCart();

            // On web, navigate immediately before showing alert
            if (Platform.OS === 'web') {
              navigation.reset({
                index: 0,
                routes: [{ name: 'Main' }],
              });
            }

            // Show alert before navigation
            showAlert(
              "Item Unavailable",
              `${cartItem.product.title} is no longer available.`,
              [
                {
                  text: "Okay",
                  onPress: () => {
                    if (Platform.OS !== 'web') {
                      navigation.popToTop();
                    }
                  }
                }
              ]
            );
          } else if (requestedQuantity > newQuantity) {
            // Quantity reduced but still available - adjust cart item quantity
            console.log('[CheckoutScreen] Product quantity reduced, adjusting cart item quantity:', {
              productId,
              requestedQuantity,
              newQuantity,
            });

            try {
              const { error: updateError } = await supabase
                .from('cart_items')
                .update({ quantity: newQuantity })
                .eq('id', cartItem.id);

              if (updateError) {
                console.error('[CheckoutScreen] Failed to adjust cart item quantity:', updateError);
              } else {
                console.log('[CheckoutScreen] Successfully adjusted cart item quantity');
                // Refresh cart to reflect the change
                await refreshCart();
                
                // On web, navigate immediately before showing alert
                if (Platform.OS === 'web') {
                  navigation.reset({
                    index: 0,
                    routes: [{ name: 'Main' }],
                  });
                }
                
                // Show alert before navigation
                showAlert(
                  "Quantity Updated",
                  `Only ${newQuantity} unit(s) of ${cartItem.product.title} are available. Your cart has been updated.`,
                  [
                    {
                      text: "Okay",
                      onPress: () => {
                        if (Platform.OS !== 'web') {
                          navigation.popToTop();
                        }
                      }
                    }
                  ]
                );
              }
            } catch (error) {
              console.error('[CheckoutScreen] Error adjusting cart item quantity:', error);
            }
          } else if (quantityChanged) {
            // Product quantity changed but requested quantity is still valid
            // Refresh cart to show updated product quantity (similar to CartsByStoreScreen)
            console.log('[CheckoutScreen] Product quantity updated, refreshing cart to show new quantity:', {
              productId,
              oldQuantity: oldRow?.quantity,
              newQuantity: newRow?.quantity,
              requestedQuantity,
            });
            
            // Refresh cart to update the displayed product quantity
            await refreshCart();
            
            // Show alert to inform user about quantity change
            showAlert(
              "Product Quantity Updated",
              `${cartItem.product.title} now has ${newQuantity} unit(s) available.`,
              [{ text: "Okay", onPress: () => {} }]
            );
          }
        }
      )
      .subscribe((status) => {
        console.log('[CheckoutScreen] Product inventory subscription status:', status);
      });

    // Store channel ref so we can unsubscribe during payment
    productInventoryChannelRef.current = channel;

    return () => {
      console.log("[CheckoutScreen] Cleaning up product inventory listener");
      supabase.removeChannel(channel);
      productInventoryChannelRef.current = null;
    };
  }, [session?.user?.id, cartItems, refreshCart, navigation]);

  // Fetch saved payment methods and customer info
  // Note: refetchOnMount is enabled in usePaymentMethods hook to ensure latest cards are loaded
  const {
    paymentMethods,
    defaultPaymentMethod,
    isLoading: isLoadingPaymentMethods,
    isFetched: isPaymentMethodsFetched,
  } = usePaymentMethods();

  // Check platform payment support (Apple Pay/Google Pay) on component mount
  useEffect(() => {
    const checkPaymentSupport = async () => {
      try {
        const platformPayResult = await isPlatformPaySupported();

        console.log(
          "💳 [PLATFORM-PAY] Support check (Apple Pay/Google Pay):",
          platformPayResult
        );

        setPlatformPaySupported(platformPayResult);
      } catch (error) {
        console.error(
          "❌ [PAYMENT-SUPPORT] Error checking platform payment support:",
          error
        );
        setPlatformPaySupported(false);
      }
    };

    checkPaymentSupport();
  }, []);

  // Set default payment method as selected when payment methods load (only on initial load)
  const hasInitializedPaymentMethod = useRef(false);
  useEffect(() => {
    // Only auto-select on initial load, not when user manually changes selection
    // Wait for payment methods to finish loading AND be fetched, and ensure we haven't initialized yet
    if (!isLoadingPaymentMethods && isPaymentMethodsFetched && !hasInitializedPaymentMethod.current) {
      console.log('[CheckoutScreen] Payment methods loaded, initializing selection:', {
        count: paymentMethods.length,
        defaultPaymentMethod,
        selectedPaymentMethodId,
        useNewCard,
        hasInitialized: hasInitializedPaymentMethod.current,
        isFetched: isPaymentMethodsFetched,
        paymentMethods: paymentMethods.map(pm => ({
          id: pm.stripe_payment_method_id,
          is_default: pm.is_default,
          last4: pm.card_last4
        }))
      });
      
      // Mark as initialized immediately to prevent re-running
      hasInitializedPaymentMethod.current = true;
      
      if (defaultPaymentMethod) {
        // Use default saved payment method
        console.log('[CheckoutScreen] Setting default payment method as selected:', defaultPaymentMethod.stripe_payment_method_id);
        setSelectedPaymentMethodId(
          defaultPaymentMethod.stripe_payment_method_id
        );
        setUseNewCard(false);
      } else if (paymentMethods.length > 0) {
        // No default set, but we have saved cards - use the most recent one (first in list)
        const mostRecentPaymentMethod = paymentMethods[0];
        console.log('[CheckoutScreen] No default payment method, selecting most recent:', mostRecentPaymentMethod.stripe_payment_method_id);
        setSelectedPaymentMethodId(
          mostRecentPaymentMethod.stripe_payment_method_id
        );
        setUseNewCard(false);
      } else {
        // No saved cards, default to Stripe payment sheet
        console.log('[CheckoutScreen] No saved payment methods, defaulting to new card');
        setUseNewCard(true);
        setSelectedPaymentMethodId(null);
      }
    }
  }, [
    defaultPaymentMethod,
    isLoadingPaymentMethods,
    isPaymentMethodsFetched,
    paymentMethods.length,
    paymentMethods,
  ]);

  // ── totals
  const merchandiseSubtotal = (cartItems || []).reduce(
    (sum, item) => sum + (item?.product?.price || 0) * (item?.quantity || 0),
    0
  );
  // Calculate seller fee (deducted from seller's revenue)
  const sellerFee = DeliveryHelpers.calculateSellerFee(merchandiseSubtotal);

  // Calculate delivery total first (needed for buyer fee calculation)
  const deliveryTotal = Number((deliveryFee || 0).toFixed(2));

  // Calculate buyer fee (15% of order + delivery)
  const buyerFee = DeliveryHelpers.calculateBuyerFee(merchandiseSubtotal, deliveryTotal);

  // Total processing fee (for display purposes)
  const totalProcessingFee = sellerFee + buyerFee;

  console.log("🧾 [CheckoutScreen] Processing fee calculation:", {
    merchandiseSubtotal,
    deliveryTotal,
    orderPlusDelivery: merchandiseSubtotal + deliveryTotal,
    buyerFeeCalculation: `(${merchandiseSubtotal + deliveryTotal}) * ${DELIVERY_CONFIG.fees.BUYER_FEE_PERCENT} = ${buyerFee}`,
    buyerFee,
    sellerFee,
    totalProcessingFee
  });

  // Use test amount if test mode is enabled
  const actualTotal = Number(
    (merchandiseSubtotal + deliveryTotal + buyerFee).toFixed(2)
  );
  const payableTotal = isTestMode
    ? DELIVERY_CONFIG.fees.TEST_PAYMENT_AMOUNT
    : actualTotal;

  const merchandiseTotalCents = Math.max(
    0,
    Math.round(merchandiseSubtotal * 100)
  );
  const deliveryFeeCents = Math.max(0, Math.round(deliveryTotal * 100));
  const vendorTransferCents = Math.max(
    0,
    Math.floor(
      merchandiseTotalCents * (1 - DELIVERY_CONFIG.fees.SELLER_FEE_PERCENT)
    )
  );

  // Phone number sanitizer for E.164 format (Uber Direct requirement)
  const toE164US = (raw: string): string => {
    let s = raw.trim().replace(/[^\d+]/g, "");

    // Already valid E.164
    if (/^\+\d{10,15}$/.test(s)) return s;

    // Remove leading +
    if (s.startsWith("+")) s = s.slice(1);

    // Case: plain 10 digits → assume U.S., add +1
    if (/^\d{10}$/.test(s)) return `+1${s}`;

    // Case: 11 digits starting with 1 → normalize to +1
    if (/^1\d{10}$/.test(s)) return `+${s}`;

    // Fallback → just add +
    return `+${s}`;
  };

  const calculateDeliveryTime = (dropoffEta?: string): string => {
    if (!dropoffEta) return "Not available";
    const eta = new Date(dropoffEta);
    if (isNaN(eta.getTime())) return "Not available";
    const diffMin = Math.round((eta.getTime() - Date.now()) / (1000 * 60));
    if (diffMin < 1) return "1-5 min";
    if (diffMin < 60) return `${Math.ceil(diffMin / 5) * 5} min`;
    const h = Math.floor(diffMin / 60);
    const m = Math.round((diffMin % 60) / 5) * 5;
    return m === 0 ? `${h}h` : `${h}h ${m}m`;
  };

  // Function to validate all cart items are within delivery range
  const validateDeliveryRange = async (): Promise<void> => {
    if (!selectedAddress) {
      throw new Error("Delivery address is required for checkout");
    }

    const userHasValidCoordinates = GeocodingService.areCoordinatesValid(
      selectedAddress.latitude,
      selectedAddress.longitude
    );

    if (!userHasValidCoordinates) {
      throw new Error(
        "Delivery address location could not be determined. Please update your delivery address with a complete address including street, city, and state."
      );
    }

    const outOfRangeItems: string[] = [];

    for (const cartItem of cartItems) {
      const vendor = cartItem.product.vendor;

      const vendorHasValidCoordinates = GeocodingService.areCoordinatesValid(
        vendor?.latitude,
        vendor?.longitude
      );

      if (vendorHasValidCoordinates) {
        // Parse delivery radius - default to 10 miles if not set or invalid
        let deliveryRadiusMiles = 10;
        if (vendor.delivery_radius) {
          const parsed = parseFloat(vendor.delivery_radius);
          if (!isNaN(parsed) && parsed > 0) {
            deliveryRadiusMiles = parsed;
          }
        }

        // Calculate distance between user and vendor
        const distance = DeliveryHelpers.calculateDistance(
          selectedAddress.latitude!,
          selectedAddress.longitude!,
          vendor.latitude!,
          vendor.longitude!
        );

        if (distance > deliveryRadiusMiles) {
          outOfRangeItems.push(
            `${cartItem.product.title} from ${
              vendor.store_name
            } (${distance.toFixed(
              1
            )} miles away, delivers within ${deliveryRadiusMiles} miles)`
          );
        }
      } else {
        console.warn(
          `[CHECKOUT] Vendor ${vendor?.store_name} missing coordinates - allowing checkout`
        );
      }
    }

    if (outOfRangeItems.length > 0) {
      throw new Error(
        `The following items cannot be delivered to your address:\n\n${outOfRangeItems.join(
          "\n\n"
        )}\n\nPlease remove these items from your cart or choose a different delivery address.`
      );
    }
  };

  // Function to validate store hours before payment
  const validateStoreHours = async (): Promise<void> => {
    if (!deliveryQuote || deliveryQuote.length === 0) {
      console.warn("[CHECKOUT] No delivery quotes available for store hours validation");
      return; // Allow checkout to proceed - will be validated server-side
    }

    const storeHoursIssues: string[] = [];

    for (const quote of deliveryQuote) {
      // Skip quotes with errors (already handled elsewhere)
      if (quote.error || !quote.quote) {
        continue;
      }

      // Get delivery hours for this vendor
      const deliveryHours = quote.delivery_hours as StoreHours | undefined;
      
      // Build vendor location for timezone detection
      const vendorLocation = {
        city: quote.vendor_city,
        state: quote.vendor_state,
        country: quote.vendor_country,
      };
      
      // Check if store is currently open (using store's timezone)
      const storeStatus = isStoreCurrentlyOpen(deliveryHours, vendorLocation);
      
      if (!storeStatus.isOpen) {
        storeHoursIssues.push(
          `${quote.vendor_name}: ${storeStatus.reason || 'Store is currently closed'}`
        );
        console.log(`🕒 [CHECKOUT] Store closed: ${quote.vendor_name} - ${storeStatus.reason}`);
        continue;
      }
      
      // Check if delivery would arrive before store closes
      if (quote.quote?.dropoff_eta && deliveryHours) {
        const deliveryTimeValidation = validateDeliveryTimeAgainstStoreHours(
          deliveryHours,
          quote.quote.dropoff_eta,
          0, // buffer minutes
          vendorLocation
        );
        
        if (!deliveryTimeValidation.isValid) {
          storeHoursIssues.push(
            `${quote.vendor_name}: ${deliveryTimeValidation.reason}`
          );
          console.log(`🕒 [CHECKOUT] Delivery time issue: ${quote.vendor_name} - ${deliveryTimeValidation.reason}`);
        }
      }
    }

    if (storeHoursIssues.length > 0) {
      throw new Error(
        `Cannot complete purchase due to store hours:\n\n${storeHoursIssues.join(
          "\n\n"
        )}\n\nPlease try again when the store is open or delivery can arrive during store hours.`
      );
    }
  };

  const processWithSavedPaymentMethod = async () => {
    const token = await getFreshToken();
    console.log("🔐 Using fresh Supabase token:", token.slice(0, 12));
    const { data: { session } } = await supabase.auth.getSession();
    const user = session?.user;
    if (!user || !token) throw new Error("Not authenticated");

    // Use the actual order total for payment
    const finalAmount = Math.round(payableTotal * 100); // Convert to cents
    if (!cartItems || cartItems.length === 0) {
      throw new Error("Cart is empty");
    }
    const vendor = cartItems[0]?.product?.vendor;
    if (!vendor) {
      throw new Error("Vendor information is missing");
    }

    // Create and confirm payment intent with saved payment method
    // NOTE: This endpoint needs to be created to handle saved payment method processing
    const paymentRes = await fetch(
      `${baseFunctionsUrl}/process-saved-payment`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          paymentMethodId: selectedPaymentMethodId,
          amount: finalAmount,
          currency: "usd",
          metadata: {
            items_subtotal: merchandiseSubtotal.toFixed(2),
            delivery_total: deliveryTotal.toFixed(2),
            seller_fee: sellerFee.toFixed(2),
            buyer_fee: buyerFee.toFixed(2),
            total_processing_fee: totalProcessingFee.toFixed(2),
            merchandise_total_cents: merchandiseTotalCents,
            delivery_fee_cents: deliveryFeeCents,
            cartItems: JSON.stringify(
              cartItems.map((i) => ({
                id: i.id,
                title: i.product?.title || "Unknown product",
                price: i.product?.price || 0,
                quantity: i.quantity,
                vendor_id: i.product.vendor_id,
              }))
            ),
            email: user?.user_metadata?.email || "guest",
          },
          connect: {
            on_behalf_of: vendor?.stripe_accountid,
            destination: vendor?.stripe_accountid,
            transfer_amount_cents: vendorTransferCents,
          },
          // Add order finalization data
          orderData: {
            total: actualTotal,
            items: cartItems,
            deliveryFee: deliveryTotal,
            breakdown: {
              items_subtotal: merchandiseSubtotal,
              delivery_total: deliveryTotal,
              seller_fee: sellerFee,
              buyer_fee: buyerFee,
              total_processing_fee: totalProcessingFee,
            },
            vendorDeliveryQuotes: (() => {
              const quotes: Record<string, any> = {};
              if (deliveryQuote && Array.isArray(deliveryQuote)) {
                for (const quote of deliveryQuote) {
                  if (quote?.vendorId && quote?.quote) {
                    quotes[quote.vendorId] = {
                      quote_id: quote.quote.quote_id,
                      pickup_latitude: quote.quote.pickup_latitude,
                      pickup_longitude: quote.quote.pickup_longitude,
                      dropoff_latitude: quote.quote.dropoff_latitude,
                      dropoff_longitude: quote.quote.dropoff_longitude,
                      pickup_phone_number: quote.quote.pickup_phone_number,
                      dropoff_phone_number: quote.quote.dropoff_phone_number,
                      fee: quote.quote.fee,
                    };
                  }
                }
              }
              return quotes;
            })(),
            deliveryAddresses: {
              pickup_address: null,
              dropoff_address: {
                street_address: selectedAddress?.street_address
                  ? [
                      selectedAddress.street_address +
                        (selectedAddress.apartment
                          ? ` #${selectedAddress.apartment}`
                          : ""),
                    ]
                  : deliveryAddress.street_address || [],
                city: selectedAddress?.city || deliveryAddress.city,
                state: selectedAddress?.state || deliveryAddress.state,
                zip_code: selectedAddress?.zip_code || deliveryAddress.zip_code,
                country: "US",
              },
            },
            deliveryContacts: {
              pickup_contact: null,
              dropoff_contact: {
                name: profile.username || "Customer",
                phone: selectedAddress?.phone_number,
              },
            },
            cart_row_ids: cartItems.map((i) => i.id),
          },
        }),
      }
    );

    const paymentJson = await paymentRes.json();
    if (!paymentRes.ok) {
      // If saved payment processing isn't available, fall back to payment sheet
      if (paymentRes.status === 404) {
        console.log(
          "💳 [SAVED-PAYMENT] Backend not available, falling back to payment sheet"
        );
        setUseNewCard(true);
        setSelectedPaymentMethodId(null);
        return;
      }
      
      // If payment method is invalid (e.g., created with different Stripe key), fall back to payment sheet
      if (paymentRes.status === 400 && paymentJson.error?.includes("Invalid payment method")) {
        console.log(
          "💳 [SAVED-PAYMENT] Payment method invalid (possibly from different Stripe account), falling back to payment sheet"
        );
        showAlert(
          "Payment Method Unavailable",
          "This saved payment method is no longer available. Please add a new payment method.",
          [
            {
              text: "OK",
              onPress: () => {
                setUseNewCard(true);
                setSelectedPaymentMethodId(null);
              }
            }
          ]
        );
        return;
      }
      
      // Handle inventory unavailable error (items sold out)
      if (paymentJson.insufficient_items || paymentJson.error?.includes("no longer available")) {
        console.log(
          "💳 [SAVED-PAYMENT] Items no longer available:",
          paymentJson.insufficient_items
        );
        
        const itemsList = paymentJson.insufficient_items?.join('\n') || 'Some items';
        const refundMessage = paymentJson.refund_initiated 
          ? '\n\nA full refund has been initiated and will appear on your statement within 5-10 business days.'
          : paymentJson.refund_error 
            ? `\n\n${paymentJson.refund_error}`
            : '';
        
        // On web, navigate immediately before showing alert
        if (Platform.OS === 'web') {
          refreshCart();
          navigation.reset({
            index: 0,
            routes: [{ name: 'Main' }],
          });
        }
        
        showAlert(
          "Items Sold Out",
          `We're sorry, but the following items were purchased by another customer:\n\n${itemsList}${refundMessage}`,
          [
            {
              text: "OK",
              onPress: () => {
                if (Platform.OS !== 'web') {
                  refreshCart();
                  navigation.popToTop();
                }
              }
            }
          ]
        );
        return;
      }
      
      throw new Error(paymentJson.error || "Payment failed");
    }

    // Handle different payment statuses
    if (paymentJson.status === "succeeded") {
      console.log(
        "💳 [SAVED-PAYMENT] Payment succeeded:",
        paymentJson.payment_intent.id
      );
      
      // Check if order was also successfully created
      if (paymentJson.order?.id) {
        console.log(
          "✅ [ORDER] Order finalized successfully:",
          paymentJson.order.id
        );

        // Track successful purchase for saved payment path
        try {
          const orderValue = actualTotal;
          const paymentMethod = paymentJson.order?.payment_method || "saved_card";

          trackEvent(
            "purchase_completed",
            {
              order_id: paymentJson.order.id,
              value: orderValue,
              currency: "USD",
              payment_method: paymentMethod,
              delivery_method: "delivery",
              item_count: cartItems.length,
              items: cartItems.map((item) => ({
                item_id: item.product.id,
                item_name: item.product.title,
                price: item.product.price,
                quantity: item.quantity,
              })),
            },
            posthog || undefined
          );
          console.log("✅ [POSTHOG] purchase_completed event tracked (saved payment)");
        } catch (analyticsError) {
          console.warn(
            "⚠️ [POSTHOG] Failed to track purchase analytics (saved payment):",
            analyticsError
          );
        }

        // Check for any warnings from order finalization
        if (paymentJson.warning) {
          console.warn(
            "⚠️ [ORDER]",
            paymentJson.warning,
            paymentJson.finalize_error
          );
        }

        // NOTE: Delivery is NOT created here anymore.
        // The seller must first accept/reject the order, then manually start delivery.
        // This allows the 10-minute accept/reject window to work properly.

        // Show alert FIRST - navigation and data refresh happen when user taps OK
        // This prevents the cart from becoming empty while still on CheckoutScreen
        
        // On web, navigate immediately before showing alert (alert callbacks don't work reliably on web)
        if (Platform.OS === 'web') {
          // Navigate to home screen
          navigation.reset({
            index: 0,
            routes: [{ name: 'Main' }],
          });
          // Refresh data after navigation (runs in background)
          Promise.all([refreshCart(), refetchProducts(), refetchOrders()])
            .then(() => {
              forceOrderBannerRefresh();
              forceProductListRefresh();
            })
            .catch((error) => console.warn("Failed to refresh some data:", error));
        }
        
        // Mark payment as successful - keep loading=true until navigation
        paymentSucceededRef.current = true;
        
        // Show success alert with navigation callback
        showAlert(
          "Payment Successful",
          "Your order has been placed! The seller has 10 minutes to accept. You can track its progress from the home screen.",
          [{ text: "OK", onPress: async () => {
            // On native platforms, navigate here
            if (Platform.OS !== 'web') {
              navigation.popToTop();
              // Refresh data after navigation (runs in background)
              try {
                await Promise.all([refreshCart(), refetchProducts(), refetchOrders()]);
                forceOrderBannerRefresh();
                forceProductListRefresh();
              } catch (error) {
                console.warn("Failed to refresh some data:", error);
              }
            }
          }}]
        );
      } else {
        // Payment succeeded but no order data - fall back to manual order processing
        console.log("💳 [SAVED-PAYMENT] Payment succeeded, processing order separately");
        paymentSucceededRef.current = true;
        await processSuccessfulPayment(paymentJson.payment_intent.id);
      }
    } else if (paymentJson.status === "requires_action") {
      // Handle 3D Secure authentication
      const { error } = await confirmPayment(
        paymentJson.payment_intent.client_secret
      );
      if (error) {
        throw new Error(error.message || "Authentication failed");
      }
      // After successful authentication, the payment should be complete
      // Note: For 3D Secure flows, we may need to check payment status again
      // or handle the order creation manually since the Edge Function won't be called again
      console.log("💳 [SAVED-PAYMENT] 3D Secure completed, processing order");
      
      // processSuccessfulPayment will show the success alert with navigation callback
      paymentSucceededRef.current = true;
      await processSuccessfulPayment(paymentJson.payment_intent.id);
    } else {
      throw new Error("Payment failed or requires further action");
    }
  };

  // Function to create delivery immediately after successful payment
  const createDeliveryAfterPayment = async (
    orderId: string,
    orderData: any,
    vendorOrderIds?: string[]
  ) => {
    try {
      console.log(
        "🚚 [BUYER-DELIVERY] Starting delivery creation for order:",
        orderId
      );
      setIsCreatingDelivery(true);

      if (!cartItems || cartItems.length === 0) {
        throw new Error("Cart is empty, cannot create delivery");
      }

      const vendor = cartItems[0]?.product?.vendor;
      if (!vendor) {
        throw new Error("Vendor information is missing");
      }
      console.log("Vendor for delivery:", vendor);
      console.log("Vendor phone_number from cart:", vendor.phone_number);

      // Prepare manifest items from cart items
      const manifestItems = cartItems.map((cartItem) =>
        DeliveryHelpers.createManifestItem(
          cartItem?.product?.title || "Unknown item",
          cartItem?.quantity || 0
        )
      );
      console.log("Manifest items:", manifestItems);

      // Construct pickup address from vendor
      const pickupAddress = {
        street_address: [vendor.street_line1],
        city: vendor.city,
        state: vendor.state,
        zip_code: vendor.zip_code,
        country: "US",
      };
      console.log("Pickup address:", pickupAddress);
      // Use the buyer's delivery address (selectedAddress or deliveryAddress)
      const dropoffAddress = {
        street_address: selectedAddress?.street_address
          ? [
              selectedAddress.street_address +
                (selectedAddress.apartment
                  ? ` #${selectedAddress.apartment}`
                  : ""),
            ]
          : deliveryAddress.street_address || [],
        city: selectedAddress?.city || deliveryAddress.city,
        state: selectedAddress?.state || deliveryAddress.state,
        zip_code: selectedAddress?.zip_code || deliveryAddress.zip_code,
        country: "US",
      };

      console.log("Dropoff address:", dropoffAddress);

      // Validate contact information
      const pickupPhone = vendor.phone_number;
      const dropoffPhone = selectedAddress?.phone_number;
      const dropoffName = profile?.username || profile?.full_name || "Customer";

      console.log("Pickup phone:", pickupPhone);
      console.log("Dropoff phone:", dropoffPhone);
      console.log("Dropoff name:", dropoffName);

      if (!pickupPhone) {
        throw new Error("Pickup contact phone number is required for delivery");
      }

      if (!dropoffPhone) {
        throw new Error(
          "Dropoff contact phone number is required for delivery"
        );
      }

      if (!dropoffName) {
        throw new Error("Customer name is required for delivery");
      }

      const normalizedPickupAddress = {
        ...pickupAddress,
        street_address: pickupAddress.street_address,
      };

      const normalizedDropoffAddress = {
        ...dropoffAddress,
        street_address: dropoffAddress.street_address,
      };

      // Prepare delivery payload for Uber API (same structure as VendorOrderDetailsScreen but without initial quote_id)
      const deliveryPayload: any = {
        pickup_address: normalizedPickupAddress,
        dropoff_address: normalizedDropoffAddress,
        pickup_name: vendor.store_name,
        pickup_phone_number: toE164US(pickupPhone),
        dropoff_name: dropoffName,
        dropoff_phone_number: toE164US(dropoffPhone),
        manifest_items: manifestItems,
        pickup_ready_dt: DeliveryHelpers.createPickupReady(useRoboCourier),
        pickup_deadline_dt: DeliveryHelpers.createPickupDeadline(useRoboCourier),
        dropoff_ready_dt: DeliveryHelpers.createDropoffReady(useRoboCourier),
        dropoff_deadline_dt: DeliveryHelpers.createDropoffDeadline(useRoboCourier),
        // Add coordinates for more accurate delivery creation
        pickup_latitude: vendor.latitude || 0,
        pickup_longitude: vendor.longitude || 0,
        dropoff_latitude: selectedAddress.latitude || 0,
        dropoff_longitude: selectedAddress.longitude || 0,
        // Photo verification at pickup
        pickup_verification: { picture: true },
        // Leave at door (automatically enables photo verification at dropoff)
        deliverable_action: 'deliverable_action_leave_at_door',
      };

      // Add test specifications (robo courier) for sandbox deliveries
      // Per Uber Direct API guide: https://developer.uber.com/docs/deliveries/guides/robocourier
      if (useRoboCourier) {
        const roboCourierSpec = DeliveryHelpers.createRoboCourierSpec('auto');
        deliveryPayload.test_specifications = roboCourierSpec.test_specifications;
        console.log("🤖 [ROBO-COURIER] ✅ Robo courier enabled for sandbox");
        console.log("🤖 [ROBO-COURIER] Spec structure:", JSON.stringify(roboCourierSpec, null, 2));
        console.log("🤖 [ROBO-COURIER] Added to deliveryPayload.test_specifications:", JSON.stringify(deliveryPayload.test_specifications, null, 2));
      } else {
        console.log("⚠️ [ROBO-COURIER] Robo courier is disabled");
      }

      console.log("📦 [CHECKOUT] Full delivery payload:", JSON.stringify(deliveryPayload, null, 2));
      console.log("📦 [CHECKOUT] Has test_specifications:", !!deliveryPayload.test_specifications);

      let deliveryResult: any;
      try {
        console.log(
          "🔄 [BUYER-DELIVERY] Generating fresh quote with UberDirectService..."
        );

        // Create base payload for quote generation (same structure as VendorOrderDetailsScreen)
        // Include test_specifications in quote request for sandbox mode
        const quoteRequest: any = {
          pickup_address: normalizedPickupAddress,
          dropoff_address: normalizedDropoffAddress,
          manifest_items: manifestItems,
          pickup_phone_number: toE164US(pickupPhone),
          dropoff_phone_number: toE164US(dropoffPhone),
          pickup_ready_dt: deliveryPayload.pickup_ready_dt,
          pickup_deadline_dt: deliveryPayload.pickup_deadline_dt,
          dropoff_ready_dt: deliveryPayload.dropoff_ready_dt,
          dropoff_deadline_dt: deliveryPayload.dropoff_deadline_dt,
          // Add coordinates for more accurate quote generation
          pickup_latitude: vendor.latitude || 0,
          pickup_longitude: vendor.longitude || 0,
          dropoff_latitude: selectedAddress.latitude || 0,
          dropoff_longitude: selectedAddress.longitude || 0,
        };
        
        // CRITICAL: Include test_specifications in quote request for sandbox mode
        // This ensures the quote is generated for a test delivery, not a real one
        if (useRoboCourier && deliveryPayload.test_specifications) {
          quoteRequest.test_specifications = deliveryPayload.test_specifications;
          console.log("🤖 [ROBO-COURIER] Adding test_specifications to quote request");
        }

        // Step 1: Generate fresh quote
        const freshQuote = await uberDirectService.getDeliveryQuote(
          quoteRequest
        );
        console.log(
          "✅ [BUYER-DELIVERY] Fresh quote generated:",
          freshQuote.quote_id
        );

        // Step 2: Create delivery with fresh quote ID (same structure as VendorOrderDetailsScreen)
        // Include vendor_order_id for pending_disbursements update
        const firstVendorOrderId = vendorOrderIds?.[0] || orderData?.vendor_order_ids?.[0];
        
        const deliveryPayloadWithFreshQuote = {
          ...deliveryPayload,
          quote_id: freshQuote.quote_id, // Use fresh quote ID
          // Add order information for status updates in edge function
          parent_order_id: orderId,
          vendor_id: vendor.id,
          vendor_order_id: firstVendorOrderId, // CRITICAL: Needed for pending_disbursements update
        };

        console.log(
          "🚀 [BUYER-DELIVERY] Creating delivery with fresh quote:",
          freshQuote.quote_id,
          "vendor_order_id:",
          firstVendorOrderId
        );

        deliveryResult = await supabase.functions.invoke(
          "uber-create-delivery",
          {
            body: deliveryPayloadWithFreshQuote,
          }
        );
      } catch (error) {
        console.error(
          "[BUYER-DELIVERY] Fresh quote generation or delivery creation failed:",
          error
        );
        throw new Error(
          `Failed to create delivery: ${
            error instanceof Error ? error.message : "Unknown error"
          }`
        );
      }

      if (deliveryResult.error) {
        console.error(
          "❌ [BUYER-DELIVERY] Edge Function error:",
          deliveryResult.error
        );
        throw new Error(
          `Failed to create delivery: ${deliveryResult.error.message}`
        );
      }

      // Check for Uber API errors in response
      if (
        deliveryResult.data?.error ||
        typeof deliveryResult.data === "string"
      ) {
        console.error(
          "❌ [BUYER-DELIVERY] Uber API error:",
          deliveryResult.data
        );
        throw new Error(
          `Uber API error: ${
            deliveryResult.data?.error || deliveryResult.data || "Unknown error"
          }`
        );
      }

      const deliveryData = deliveryResult.data;
      const actualDeliveryId =
        deliveryData?.id ||
        deliveryData?.delivery_id ||
        deliveryData?.delivery?.id;
      const actualTrackingUrl =
        deliveryData?.tracking_url || deliveryData?.tracking?.url;

      if (!actualDeliveryId) {
        console.error(
          "❌ [BUYER-DELIVERY] No delivery ID in response:",
          deliveryData
        );
        throw new Error("Uber API did not return a valid delivery ID");
      }

      console.log("✅ [BUYER-DELIVERY] Delivery created successfully:", {
        deliveryId: actualDeliveryId,
        trackingUrl: actualTrackingUrl,
      });
      
      // Validate robo courier in response
      console.log("🤖 [ROBO-COURIER] Checking delivery response for test indicators...");
      console.log("🤖 [ROBO-COURIER] Full delivery response:", JSON.stringify(deliveryData, null, 2));
      if (deliveryData.test_specifications) {
        console.log("🤖 [ROBO-COURIER] ✅ test_specifications in delivery response:", JSON.stringify(deliveryData.test_specifications, null, 2));
      }
      if (deliveryData.robo_courier) {
        console.log("🤖 [ROBO-COURIER] ✅ robo_courier info in response:", JSON.stringify(deliveryData.robo_courier, null, 2));
      }
      if (deliveryData.status) {
        console.log("🤖 [ROBO-COURIER] Initial delivery status:", deliveryData.status);
      }

      // Update both orders and vendor_orders tables using our Supabase function
      const updateResult = await supabase.functions.invoke(
        "update-order-delivery",
        {
          body: {
            order_id: orderId,
            vendor_id: vendor.id,
            delivery_id: actualDeliveryId,
            tracking_url: actualTrackingUrl,
            status: "pickup_pending",
          },
        }
      );

      if (updateResult.error) {
        console.error(
          "❌ [BUYER-DELIVERY] Error updating order delivery info:",
          updateResult.error
        );
        throw new Error(
          `Failed to update order delivery info: ${updateResult.error.message}`
        );
      }

      console.log(
        "✅ [BUYER-DELIVERY] Successfully updated order and vendor orders:",
        updateResult.data
      );

      console.log(
        "✅ [BUYER-DELIVERY] Successfully updated order status to pickup_pending for vendor:",
        vendor.id
      );
    } catch (error) {
      console.error("❌ [BUYER-DELIVERY] Error in delivery creation:", error);
      // Don't throw here to avoid disrupting the payment flow
    } finally {
      setIsCreatingDelivery(false);
    }
  };

  const processSuccessfulPayment = async (paymentIntentId: string) => {
    const token = await getFreshToken();
    console.log("🔐 Using fresh Supabase token:", token.slice(0, 12));

    // Prepare delivery data similar to the existing flow
    const vendorDeliveryQuotes: Record<string, any> = {};

    // Build delivery quotes per vendor (if available)
    if (deliveryQuote && Array.isArray(deliveryQuote)) {
      for (const quote of deliveryQuote) {
        if (quote?.vendorId && quote?.quote) {
          vendorDeliveryQuotes[quote.vendorId] = {
            quote_id: quote.quote.quote_id,
            pickup_latitude: quote.quote.pickup_latitude,
            pickup_longitude: quote.quote.pickup_longitude,
            dropoff_latitude: quote.quote.dropoff_latitude,
            dropoff_longitude: quote.quote.dropoff_longitude,
            pickup_phone_number: quote.quote.pickup_phone_number,
            dropoff_phone_number: quote.quote.dropoff_phone_number,
            fee: quote.quote.fee,
          };
        }
      }
    }

    // Prepare delivery addresses
    const deliveryAddresses = {
      pickup_address: null, // Will be populated with vendor addresses in finalize-order
      dropoff_address: {
        street_address: selectedAddress?.street_address
          ? [
              selectedAddress.street_address +
                (selectedAddress.apartment
                  ? ` #${selectedAddress.apartment}`
                  : ""),
            ]
          : deliveryAddress.street_address || [],
        city: selectedAddress?.city || deliveryAddress.city,
        state: selectedAddress?.state || deliveryAddress.state,
        zip_code: selectedAddress?.zip_code || deliveryAddress.zip_code,
        country: DELIVERY_CONFIG.address.DEFAULT_COUNTRY,
      },
    };

    // Prepare delivery contacts
    const deliveryContacts = {
      pickup_contact: null, // Will be populated with vendor info in finalize-order
      dropoff_contact: {
        name: profile.username || "Customer",
        phone: selectedAddress?.phone_number,
      },
    };

    // Finalize order
    const finalizeRes = await fetch(
      `${baseFunctionsUrl}/finalize-order`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          "X-Idempotency-Key": `finalize-${paymentIntentId}`,
        },
        body: JSON.stringify({
          total: actualTotal,
          items: cartItems,
          deliveryFee: deliveryTotal,
          payment_intent_id: paymentIntentId,
          payment_id: null,
          payment_method: "card",
          payment_status: "succeeded",
          receipt_url: null,
          breakdown: {
            items_subtotal: merchandiseSubtotal,
            delivery_total: deliveryTotal,
            seller_fee: sellerFee,
            buyer_fee: buyerFee,
            total_processing_fee: totalProcessingFee,
          },
          delivery_quotes: vendorDeliveryQuotes,
          delivery_addresses: deliveryAddresses,
          delivery_contacts: deliveryContacts,
        }),
      }
    );

    if (!finalizeRes.ok) {
      const errorJson = await finalizeRes.json().catch(() => ({}));
      throw new Error(errorJson.error || "Failed to finalize order");
    }

    const finalizeJson = await finalizeRes.json();
    const orderId = finalizeJson?.order_id;

    console.log("✅ [ORDER] Order finalized successfully:", orderId);

    // Track successful purchase for manual finalize path
    if (orderId) {
      try {
        const orderValue = actualTotal;
        const paymentMethod = finalizeJson?.payment_method || "card";

        trackEvent(
          "purchase_completed",
          {
            order_id: orderId,
            value: orderValue,
            currency: "USD",
            payment_method: paymentMethod,
            delivery_method: "delivery",
            item_count: cartItems.length,
            items: cartItems.map((item) => ({
              item_id: item.product.id,
              item_name: item.product.title,
              price: item.product.price,
              quantity: item.quantity,
            })),
          },
          posthog || undefined
        );
        console.log("✅ [POSTHOG] purchase_completed event tracked (manual finalize)");
      } catch (analyticsError) {
        console.warn(
          "⚠️ [POSTHOG] Failed to track purchase analytics (manual finalize):",
          analyticsError
        );
      }
    }

    // NOTE: Delivery is NOT created here anymore.
    // The seller must first accept/reject the order, then manually start delivery.

    // On web, navigate immediately before showing alert (alert callbacks don't work reliably on web)
    if (Platform.OS === 'web') {
      // Navigate to home screen
      navigation.reset({
        index: 0,
        routes: [{ name: 'Main' }],
      });
      // Refresh data after navigation (runs in background)
      Promise.all([refreshCart(), refetchProducts(), refetchOrders()])
        .then(() => {
          forceOrderBannerRefresh();
          forceProductListRefresh();
        })
        .catch((error) => console.warn("Failed to refresh some data:", error));
    }

    // Show success alert - navigation and data refresh happen when user taps OK
    showAlert(
      "Payment Successful",
      "Your order has been placed! The seller has 10 minutes to accept. You can track its progress from the home screen.",
      [{ text: "OK", onPress: async () => {
        // On native platforms, navigate here
        if (Platform.OS !== 'web') {
          navigation.popToTop();
          // Refresh data after navigation (runs in background)
          try {
            await Promise.all([refreshCart(), refetchProducts(), refetchOrders()]);
            forceOrderBannerRefresh();
            forceProductListRefresh();
          } catch (error) {
            console.warn("Failed to refresh some data:", error);
          }
        }
      } }]
    );
  };

  // Validate inventory before payment
  const validateInventoryBeforePayment = async (): Promise<boolean> => {
    if (!cartItems || cartItems.length === 0) {
      showAlert("Cart Empty", "Your cart is empty. Redirecting to cart.", [
        { text: "OK", onPress: () => navigation.goBack() }
      ]);
      return false;
    }

    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user?.id) {
      showAlert("Authentication Error", "Please log in to continue.");
      return false;
    }

    // Re-fetch cart items with latest product data
    const { data: latestCartItems, error: fetchError } = await supabase
      .from('cart_items')
      .select(`
        *,
        product:products(
          id,
          title,
          quantity,
          status
        )
      `)
      .eq('user_id', session.user.id)
      .in('product_id', cartItems.map(item => item.product.id));

    if (fetchError) {
      console.error('[CheckoutScreen] Failed to validate inventory:', fetchError);
      showAlert("Error", "Failed to validate inventory. Please try again.");
      return false;
    }

    // Check if any items are missing (were deleted)
    if (!latestCartItems || latestCartItems.length === 0) {
      showAlert(
        "Cart Empty",
        "Your cart items are no longer available. Redirecting to cart.",
        [
          {
            text: "OK",
            onPress: () => {
              refreshCart();
              navigation.goBack();
            },
          },
        ]
      );
      return false;
    }

    const invalidItems: string[] = [];
    for (const item of latestCartItems) {
      const product = item.product;
      if (!product) {
        invalidItems.push(`Item ${item.id} (product not found)`);
        continue;
      }

      const availableQty = Number(product.quantity ?? 0);
      const requestedQty = Number(item.quantity ?? 0);

      if (product.status === 'sold' || product.status === 'archived') {
        invalidItems.push(`${product.title || 'Item'} (no longer available)`);
      } else if (availableQty < requestedQty) {
        invalidItems.push(
          `${product.title || 'Item'} (only ${availableQty} available, ${requestedQty} requested)`
        );
      }
    }

    if (invalidItems.length > 0) {
      showAlert(
        "Items No Longer Available",
        `The following items are no longer available:\n\n${invalidItems.join('\n')}\n\nPlease return to your cart to remove them.`,
        [
          {
            text: "OK",
            onPress: () => {
              refreshCart();
              navigation.goBack();
            },
          },
        ]
      );
      return false;
    }

    return true;
  };

  // Re-validate inventory AFTER payment succeeds (before finalizing order)
  // This is critical to prevent double-purchases when another user buys the item
  // while the current user has the Stripe payment sheet open
  const validateInventoryAfterPayment = async (): Promise<{ isValid: boolean; invalidItems: string[] }> => {
    console.log('[CheckoutScreen] 🔒 Validating inventory AFTER payment...');
    
    if (!cartItems || cartItems.length === 0) {
      return { isValid: false, invalidItems: ['Cart is empty'] };
    }

    const productIds = cartItems.map(item => item.product.id);
    
    // Fetch latest product data directly (not from cart - cart may already be empty)
    const { data: products, error: fetchError } = await supabase
      .from('products')
      .select('id, title, quantity, status')
      .in('id', productIds);

    if (fetchError) {
      console.error('[CheckoutScreen] Failed to validate inventory after payment:', fetchError);
      // If we can't validate, return invalid to be safe
      return { isValid: false, invalidItems: ['Failed to verify item availability'] };
    }

    const invalidItems: string[] = [];
    const productMap = new Map(products?.map(p => [p.id, p]) || []);

    for (const cartItem of cartItems) {
      const product = productMap.get(cartItem.product.id);
      
      if (!product) {
        invalidItems.push(`${cartItem.product.title || 'Item'} (no longer exists)`);
        continue;
      }

      const availableQty = Number(product.quantity ?? 0);
      const requestedQty = Number(cartItem.quantity ?? 0);

      if (product.status === 'sold' || product.status === 'archived') {
        console.log(`[CheckoutScreen] ❌ Product ${product.title} is ${product.status}`);
        invalidItems.push(`${product.title || 'Item'} (sold out)`);
      } else if (availableQty < requestedQty) {
        console.log(`[CheckoutScreen] ❌ Product ${product.title}: only ${availableQty} available, ${requestedQty} requested`);
        invalidItems.push(
          `${product.title || 'Item'} (only ${availableQty} available, needed ${requestedQty})`
        );
      } else {
        console.log(`[CheckoutScreen] ✅ Product ${product.title}: ${availableQty} available`);
      }
    }

    if (invalidItems.length > 0) {
      console.log('[CheckoutScreen] ❌ Inventory validation FAILED after payment:', invalidItems);
      return { isValid: false, invalidItems };
    }

    console.log('[CheckoutScreen] ✅ Inventory validation passed after payment');
    return { isValid: true, invalidItems: [] };
  };

  // Refund a payment intent when items are no longer available
  const refundPaymentIntent = async (paymentIntentId: string, reason: string): Promise<boolean> => {
    console.log(`[CheckoutScreen] 💸 Initiating refund for payment ${paymentIntentId}: ${reason}`);
    
    try {
      const token = await getFreshToken();
      
      const refundRes = await fetch(`${baseFunctionsUrl}/refund-payment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          payment_intent_id: paymentIntentId,
          reason: 'items_unavailable',
          metadata: {
            refund_reason: reason,
            refund_initiated_at: new Date().toISOString(),
          },
        }),
      });

      if (!refundRes.ok) {
        const errorJson = await refundRes.json().catch(() => ({}));
        console.error('[CheckoutScreen] Refund request failed:', errorJson);
        // Even if refund fails, we shouldn't proceed with the order
        // The user will need to contact support
        return false;
      }

      console.log('[CheckoutScreen] ✅ Refund initiated successfully');
      return true;
    } catch (error) {
      console.error('[CheckoutScreen] Error initiating refund:', error);
      return false;
    }
  };

  // Helper function to pause realtime listeners during payment
  // This prevents the listeners from navigating away when we empty our own cart
  const pauseRealtimeListeners = () => {
    if (cartDeletionChannelRef.current) {
      supabase.removeChannel(cartDeletionChannelRef.current);
      cartDeletionChannelRef.current = null;
    }
    if (productInventoryChannelRef.current) {
      supabase.removeChannel(productInventoryChannelRef.current);
      productInventoryChannelRef.current = null;
    }
  };

  const initializePayment = async () => {
    // Block payment if store hours changed during checkout
    if (storeHoursBlocked) {
      showAlert(
        "Cannot Complete Payment",
        storeHoursBlockedReason || "Store hours have changed. Please go back to your cart to verify availability.",
        [
          {
            text: "Go Back to Cart",
            onPress: () => navigation.goBack(),
          },
        ]
      );
      return;
    }

    // Mark that we're processing a payment to prevent the "cart empty" alert
    isProcessingPaymentRef.current = true;
    // Reset payment success flag
    paymentSucceededRef.current = false;
    
    try {
      setLoading(true);
      // Freeze the display totals so they don't change during/after payment
      frozenTotalsRef.current = {
        merchandiseSubtotal,
        deliveryTotal,
        buyerFee,
        payableTotal
      };
      // Pause realtime listeners to prevent them from navigating when we empty our own cart
      // The listeners will be cleaned up naturally when the component unmounts after navigation
      pauseRealtimeListeners();

      // Validate inventory before processing payment
      const isValid = await validateInventoryBeforePayment();
      if (!isValid) {
        setLoading(false);
        // Reset payment processing flag so cart empty detection works again
        isProcessingPaymentRef.current = false;
        return;
      }

      // Validate delivery range before processing payment
      await validateDeliveryRange();

      // Validate store hours before processing payment
      await validateStoreHours();

      const token = await getFreshToken();
      console.log("🔐 Using fresh Supabase token:", token.slice(0, 12));
      const { data: { session } } = await supabase.auth.getSession();
      const user = session?.user;
      if (!user || !token) throw new Error("Not authenticated");

      // Check if we're using a saved payment method
      if (selectedPaymentMethodId && !useNewCard) {
        // Track payment attempt with saved payment method
        try {
          trackEvent(
            "payment_attempted",
            {
              amount: payableTotal,
              currency: "USD",
              method_type: "saved_card",
              delivery_fee: deliveryTotal,
              items_subtotal: merchandiseSubtotal,
            },
            posthog || undefined
          );
        } catch (analyticsError) {
          console.warn(
            "⚠️ [POSTHOG] Failed to track payment_attempted (saved payment):",
            analyticsError
          );
        }

        // Process payment with saved payment method
        await processWithSavedPaymentMethod();
        return;
      }

      // Use the actual order total for payment
      const finalAmount = Math.round(payableTotal * 100); // Convert to cents
      const vendor = cartItems[0]?.product.vendor;

      // 1) Create PaymentIntent (serverless func) with improved error logging and alerts
      // Track payment attempt for Payment Sheet path
      try {
        trackEvent(
          "payment_attempted",
          {
            amount: payableTotal,
            currency: "USD",
            method_type: "payment_sheet",
            delivery_fee: deliveryTotal,
            items_subtotal: merchandiseSubtotal,
          },
          posthog || undefined
        );
      } catch (analyticsError) {
        console.warn(
          "⚠️ [POSTHOG] Failed to track payment_attempted (payment sheet):",
          analyticsError
        );
      }

      const createPIRes = await fetch(`${baseFunctionsUrl}/create-payment-intent`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          amount: finalAmount,
          currency: "usd",
          metadata: {
            items_subtotal: merchandiseSubtotal.toFixed(2),
            delivery_total: deliveryTotal.toFixed(2),
            seller_fee: sellerFee.toFixed(2),
            buyer_fee: buyerFee.toFixed(2),
            total_processing_fee: totalProcessingFee.toFixed(2),
            merchandise_total_cents: merchandiseTotalCents,
            delivery_fee_cents: deliveryFeeCents,
            cartItems: JSON.stringify(
              cartItems.map((i) => ({
                id: i.id,
                title: i.product?.title || "Unknown product",
                price: i.product?.price || 0,
                quantity: i.quantity,
                vendor_id: i.product.vendor_id,
              }))
            ),
            email: user?.user_metadata?.email || "guest",
          },
          connect: {
            on_behalf_of: vendor?.stripe_accountid,
            destination: vendor?.stripe_accountid,
            transfer_amount_cents: vendorTransferCents,
          },
        }),
      });

      let createPIJson: any = {};
      try {
        createPIJson = await createPIRes.json();
      } catch (parseErr) {
        console.error("❌ [create-payment-intent] Failed to parse JSON:", parseErr);
      }

      if (!createPIRes.ok) {
        console.error(
          "❌ [create-payment-intent] Request failed",
          createPIRes.status,
          createPIJson
        );
        // Extract error message properly - Stripe errors have nested structure
        const errorMessage = 
          (typeof createPIJson?.error === 'string' ? createPIJson.error : createPIJson?.error?.message) ||
          createPIJson?.message ||
          (createPIJson ? JSON.stringify(createPIJson) : null) ||
          `Payment intent creation failed with status ${createPIRes.status}`;
        showAlert("Error", errorMessage);
        throw new Error(errorMessage);
      }

      if (!createPIJson.clientSecret) {
        console.warn(
          "⚠️ [create-payment-intent] No clientSecret returned:",
          createPIJson
        );
        showAlert(
          "Error",
          createPIJson?.error ||
            "No client secret returned from create-payment-intent function."
        );
        throw new Error(
          createPIJson?.error || "Missing clientSecret in payment intent response"
        );
      }

      console.log("✅ [create-payment-intent] Response:", createPIJson);
      const clientSecret: string = createPIJson.clientSecret;

      // 2) Init + present PaymentSheet
      console.log(
        "💳 [PAYMENT-SHEET] Initializing with platform payment support (Apple Pay/Google Pay)"
      );
      console.log("💳 [PLATFORM-PAY] Supported:", platformPaySupported);

      const { error: initError } = await initPaymentSheet({
        merchantDisplayName: "JadeTrade",
        paymentIntentClientSecret: clientSecret,
        allowsDelayedPaymentMethods: true,
        returnURL: "com.jadetrade://stripe-redirect",
        style: "alwaysLight",
        defaultBillingDetails: {
          name: user?.user_metadata?.full_name || "",
          email: user?.user_metadata?.email || "",
        },
        // Enable Apple Pay and Google Pay
        applePay: {
          merchantCountryCode: "US",
        },
        googlePay: {
          merchantCountryCode: "US",
          testEnv: false, // Production environment
        },
      });
      if (initError)
        throw new Error(`Payment sheet init error: ${initError.message}`);

      const { error: presentError } = await presentPaymentSheet();
      if (presentError) {
        // Check if user cancelled the payment
        if (
          presentError.code === "Canceled" ||
          presentError.message.includes("canceled") ||
          presentError.message.includes("cancelled") ||
          presentError.message.includes("The payment was canceled")
        ) {
          console.log("💳 [PAYMENT] User cancelled payment");
          // Reset payment processing flag so cart empty detection works again
          isProcessingPaymentRef.current = false;
          return; // Exit gracefully without showing error
        }

        if (presentError.message.includes("No such paymentintent")) {
          showAlert(
            "Environment Mismatch",
            "Check Stripe test/live keys alignment between app and Supabase."
          );
          // Reset payment processing flag so cart empty detection works again
          isProcessingPaymentRef.current = false;
          return;
        }
        throw new Error(`Payment presentation error: ${presentError.message}`);
      }

      // 3) Payment sheet completed successfully - now validate inventory AGAIN
      // This is CRITICAL: Another user may have purchased the item while the payment sheet was open
      const payment_intent_id = clientSecret.split("_secret_")[0];
      
      console.log("💳 [PAYMENT] Payment sheet completed, validating inventory before finalizing order...");
      const { isValid: inventoryStillValid, invalidItems } = await validateInventoryAfterPayment();
      
      if (!inventoryStillValid) {
        console.log("❌ [PAYMENT] Inventory no longer available after payment, initiating refund...");
        
        // Track the failed purchase due to inventory
        try {
          trackEvent(
            "purchase_failed_inventory_unavailable",
            {
              payment_intent_id,
              invalid_items: invalidItems,
              amount: payableTotal,
            },
            posthog || undefined
          );
        } catch (analyticsError) {
          console.warn("⚠️ [POSTHOG] Failed to track inventory failure:", analyticsError);
        }
        
        // Initiate refund
        const refundSuccess = await refundPaymentIntent(
          payment_intent_id,
          `Items no longer available: ${invalidItems.join(', ')}`
        );
        
        // Reset payment processing flag
        isProcessingPaymentRef.current = false;
        
        // On web, navigate immediately before showing alert
        if (Platform.OS === 'web') {
          refreshCart();
          navigation.reset({
            index: 0,
            routes: [{ name: 'Main' }],
          });
        }
        
        // Show appropriate message to user
        showAlert(
          "Items Sold Out",
          `We're sorry, but the following items were purchased by another customer while you were completing payment:\n\n${invalidItems.join('\n')}\n\n${refundSuccess ? 'A full refund has been initiated and will appear on your statement within 5-10 business days.' : 'Please contact support for a refund.'}`,
          [
            {
              text: "OK",
              onPress: () => {
                if (Platform.OS !== 'web') {
                  refreshCart();
                  navigation.popToTop();
                }
              },
            },
          ]
        );
        return;
      }
      
      console.log("✅ [PAYMENT] Inventory validated, proceeding to finalize order...");

      // Prepare delivery data for vendor-initiated delivery creation
      const vendorDeliveryQuotes: Record<string, any> = {};

      // Build delivery quotes per vendor
      if (deliveryQuote && Array.isArray(deliveryQuote)) {
        for (const quote of deliveryQuote) {
          if (quote?.vendorId && quote?.quote) {
            vendorDeliveryQuotes[quote.vendorId] = {
              quote_id: quote.quote.quote_id,
              pickup_latitude: quote.quote.pickup_latitude,
              pickup_longitude: quote.quote.pickup_longitude,
              dropoff_latitude: quote.quote.dropoff_latitude,
              dropoff_longitude: quote.quote.dropoff_longitude,
              pickup_phone_number: quote.quote.pickup_phone_number,
              dropoff_phone_number: quote.quote.dropoff_phone_number,
              fee: quote.quote.fee,
            };
          }
        }
      }

      // Prepare delivery addresses
      const deliveryAddresses = {
        pickup_address: null, // Will be populated with vendor addresses in finalize-order
        dropoff_address: {
          street_address: selectedAddress?.street_address
            ? [
                selectedAddress.street_address +
                  (selectedAddress.apartment
                    ? ` #${selectedAddress.apartment}`
                    : ""),
              ]
            : deliveryAddress.street_address || [],
          city: selectedAddress?.city || deliveryAddress.city,
          state: selectedAddress?.state || deliveryAddress.state,
          zip_code: selectedAddress?.zip_code || deliveryAddress.zip_code,
          country: DELIVERY_CONFIG.address.DEFAULT_COUNTRY,
        },
      };

      // Prepare delivery contacts
      const deliveryContacts = {
        pickup_contact: null, // Will be populated with vendor info in finalize-order
        dropoff_contact: {
          name: profile.username || "Customer",
          phone: selectedAddress?.phone_number,
        },
      };

      const finalizeRes = await fetch(
        `${baseFunctionsUrl}/finalize-order`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
            "X-Idempotency-Key": `finalize-${payment_intent_id}`, // защитимся от повторов
          },
          body: JSON.stringify({
            total: actualTotal, // Always use actual total for order record
            items: cartItems, // функция сама нормализует
            deliveryFee: deliveryTotal,
            payment_intent_id,
            payment_id: null, // при необходимости передай charge/capture id
            payment_method: "card",
            payment_status: "succeeded",
            receipt_url: createPIJson?.receiptUrl ?? null,
            breakdown: {
              items_subtotal: merchandiseSubtotal,
              delivery_total: deliveryTotal,
              seller_fee: sellerFee,
              buyer_fee: buyerFee,
              total_processing_fee: totalProcessingFee,
            },
            stripe: {
              on_behalf_of: vendor?.stripe_accountid ?? null,
              transfer_amount_cents: vendorTransferCents,
              currency: "usd",
            },
            vendorLogistics: {}, // Empty - no immediate delivery creation
            // NEW: Delivery data for vendor-initiated delivery
            vendorDeliveryQuotes,
            deliveryAddresses,
            deliveryContacts,
            cart_row_ids: cartItems.map((i) => i.id), // удалим именно эти строки корзины
          }),
        }
      );

      if (!finalizeRes.ok) {
        const j = await finalizeRes.json().catch(() => ({}));
        console.warn("Finalize order failed:", j);
        
        // Handle inventory failure - requires refund
        if (j.requires_refund) {
          console.log("❌ [PAYMENT] Inventory unavailable at finalize, initiating refund...");
          
          const refundSuccess = await refundPaymentIntent(
            payment_intent_id,
            `Items no longer available: ${j.insufficient_items?.join(', ') || 'Unknown items'}`
          );
          
          // Reset payment processing flag
          isProcessingPaymentRef.current = false;
          
          // On web, navigate immediately before showing alert
          if (Platform.OS === 'web') {
            refreshCart();
            navigation.reset({
              index: 0,
              routes: [{ name: 'Main' }],
            });
          }
          
          const itemsList = j.insufficient_items?.join('\n') || 'Some items';
          showAlert(
            "Items Sold Out",
            `We're sorry, but the following items were purchased by another customer:\n\n${itemsList}\n\n${refundSuccess ? 'A full refund has been initiated and will appear on your statement within 5-10 business days.' : 'Please contact support for a refund.'}`,
            [
              {
                text: "OK",
                onPress: () => {
                  if (Platform.OS !== 'web') {
                    refreshCart();
                    navigation.popToTop();
                  }
                },
              },
            ]
          );
          return;
        }
        
        showAlert(
          "Payment completed",
          "We received your payment, but saving the order ran into a hiccup. Your order list may take a moment to update."
        );
      } else {
        // Payment successful - order created with delivery quotes stored for vendor-initiated delivery
        console.log(
          "💳 [PAYMENT] Payment successful, order created with delivery data"
        );

        // Get the order ID from finalize response
        // NOTE: Delivery is NOT created here anymore.
        // The seller must first accept/reject the order, then manually start delivery.
        const finalizeJson = await finalizeRes.json();
        const orderId = finalizeJson?.order_id;

        if (orderId) {
          // Track successful purchase analytics
          try {
            const orderValue = actualTotal;
            const paymentMethod = finalizeJson?.payment_method || "stripe";

            // Track purchase_completed event in PostHog
            // Use posthog if available, otherwise trackEvent will use global instance
            trackEvent("purchase_completed", {
              order_id: orderId,
              value: orderValue,
              currency: "USD",
              payment_method: paymentMethod,
              delivery_method: deliveryMethod || "unknown",
              item_count: cartItems.length,
              items: cartItems.map((item) => ({
                item_id: item.product.id,
                item_name: item.product.title,
                price: item.product.price,
                quantity: item.quantity,
              })),
            }, posthog || undefined);
            console.log("✅ [POSTHOG] purchase_completed event tracked");
          } catch (analyticsError) {
            console.warn(
              "⚠️ [POSTHOG] Failed to track purchase analytics:",
              analyticsError
            );
            // Don't fail the whole process if analytics fails
          }

          // Send order confirmation notification to user
          try {
            await sendOrderUpdate(
              session?.user?.id || "",
              orderId,
              "paid",
              "Your order has been placed! Waiting for seller to accept."
            );
            console.log("✅ [NOTIFICATION] Order placed notification sent");
          } catch (notifError) {
            console.warn(
              "⚠️ [NOTIFICATION] Failed to send order notification:",
              notifError
            );
            // Don't fail the whole process if notification fails
          }
        }

        // On web, navigate immediately before showing alert (alert callbacks don't work reliably on web)
        if (Platform.OS === 'web') {
          // Navigate to home screen
          navigation.reset({
            index: 0,
            routes: [{ name: 'Main' }],
          });
          // Refresh data after navigation (runs in background)
          Promise.all([refreshCart(), refetchProducts(), refetchOrders()])
            .then(() => {
              forceOrderBannerRefresh();
              forceProductListRefresh();
            })
            .catch((error) => console.warn("Failed to refresh some data:", error));
        }

        // Mark payment as successful - keep loading=true until navigation
        paymentSucceededRef.current = true;
        
        // Show success alert - navigation and data refresh happen when user taps OK
        showAlert(
          "Payment Successful",
          "Your order has been placed! The seller has 10 minutes to accept. You can track its progress from the home screen.",
          [{ text: "OK", onPress: async () => {
            // On native platforms, navigate here
            if (Platform.OS !== 'web') {
              navigation.popToTop();
              // Refresh data after navigation (runs in background)
              try {
                await Promise.all([refreshCart(), refetchProducts(), refetchOrders()]);
                forceOrderBannerRefresh();
                forceProductListRefresh();
              } catch (error) {
                console.warn("Failed to refresh some data:", error);
              }
            }
          } }]
        );
      }
    } catch (error: any) {
      console.log("Payment error:", error);
      // Reset payment processing flag so cart empty detection works again
      isProcessingPaymentRef.current = false;
      
      // Note: Realtime listeners were unsubscribed at the start of payment
      // They won't re-subscribe automatically, which is fine since the user
      // will either retry (and we'll call pauseRealtimeListeners again) or go back

      // Don't show alert for user cancellation
      if (
        error?.message &&
        (error.message.includes("canceled") ||
          error.message.includes("cancelled") ||
          error.message.includes("The payment was canceled"))
      ) {
        console.log("💳 [PAYMENT] Payment cancelled by user - no error shown");
        return;
      }

      // Track failed payment attempts (non-cancellation)
      try {
        trackEvent(
          "payment_failed",
          {
            reason: error?.message ?? "Unknown error",
            amount: payableTotal,
            currency: "USD",
            delivery_fee: deliveryTotal,
            items_subtotal: merchandiseSubtotal,
          },
          posthog || undefined
        );
      } catch (analyticsError) {
        console.warn(
          "⚠️ [POSTHOG] Failed to track payment_failed:",
          analyticsError
        );
      }

      // Show error alert for actual errors
      showAlert(
        "Error",
        error?.message ?? "An error occurred during payment"
      );
    } finally {
      // Only set loading to false on failure - keep it true on success until navigation
      // This prevents the button from showing a different dollar amount after success
      if (!paymentSucceededRef.current) {
        setLoading(false);
        // Clear frozen totals on failure so fresh values are shown
        frozenTotalsRef.current = null;
      }
    }
  };
  return (
    <SafeAreaView style={styles.safeArea} edges={["bottom"]}>
      <ScrollView style={styles.container}>
        <View style={styles.section}>
          <Text style={styles.title}>Order Summary</Text>

          {cartItems && cartItems.length > 0 ? (
            cartItems.map((item) => {
              if (!item?.product) return null;
              // Transform image URL for web compatibility (HEIC -> JPEG)
              const imageUrl = Platform.OS === 'web' 
                ? getWebImageUrl(item.product.images?.[0]) 
                : item.product.images?.[0];
              return (
                <View key={item.id} style={styles.cartItem}>
                  <Image
                    source={{ uri: imageUrl }}
                    style={[
                      styles.itemImage,
                      !imageUrl && styles.placeholderImage,
                    ]}
                    contentFit="cover"
                    cachePolicy="memory-disk"
                    transition={0}
                  />
                  <View style={styles.itemDetails}>
                    <Text style={styles.itemName}>{item.product.title || "Unknown Item"}</Text>
                    <Text style={styles.itemQuantity}>
                      Quantity: {item.quantity || 0}
                    </Text>
                    <Text style={styles.itemPrice}>
                      {formatPrice(item.product.price || 0)}
                    </Text>
                  </View>
                </View>
              );
            })
          ) : (
            <Text style={styles.emptyCartText}>Your cart is empty</Text>
          )}

          {deliveryQuote && (
            <View style={styles.deliveryInfo}>
              <Text style={styles.deliveryTitle}>Delivery Details</Text>
              {deliveryQuote.map((quote: any, index: number) => {
                const vendorId = quote.vendorId;
                const vendorItem = cartItems.find(
                  (i) => i.product.vendor_id === vendorId
                );
                const vendorName =
                  vendorItem?.product.vendor.store_name || "Vendor";
                const vendorStreetAddress =
                  vendorItem?.product.vendor.street_line1 || "Unknown Location";
                return (
                  <View key={index} style={styles.deliveryItem}>
                    <Text style={styles.vendorName}>{vendorName}</Text>
                    <Text style={styles.deliveryTime}>
                      Estimated delivery:{" "}
                      {calculateDeliveryTime(quote?.quote?.dropoff_eta)}
                    </Text>
                    <Text style={styles.deliveryFee}>
                      Fee: {formatPrice((quote?.quote?.fee || 0) / 100)}
                    </Text>
                  </View>
                );
              })}
            </View>
          )}

          <View style={styles.totalContainer}>
            <View style={styles.summaryRow}>
              <Text style={styles.summaryLabel}>Items</Text>
              <Text style={styles.summaryValue}>
                {formatPrice(frozenTotalsRef.current?.merchandiseSubtotal ?? merchandiseSubtotal)}
              </Text>
            </View>
            <View style={styles.summaryRow}>
              <Text style={styles.summaryLabel}>Delivery</Text>
              <Text style={styles.summaryValue}>
                {formatPrice(frozenTotalsRef.current?.deliveryTotal ?? deliveryTotal)}
              </Text>
            </View>
            <View style={styles.summaryRow}>
              <Text style={styles.summaryLabel}>Processing Fee</Text>
              <Text style={styles.summaryValue}>{formatPrice(frozenTotalsRef.current?.buyerFee ?? buyerFee)}</Text>
            </View>
            <View style={[styles.summaryRow, styles.totalRow]}>
              <Text style={styles.totalLabel}>Total</Text>
              <Text style={styles.totalAmount}>{formatPrice(frozenTotalsRef.current?.payableTotal ?? payableTotal)}</Text>
            </View>
          </View>
        </View>

        <View style={styles.infoContainer}>
          <View style={styles.infoBox}>
            <Feather name="info" size={20} color={colors.primary} />
            <Text style={styles.infoText}>
              Your payment is securely processed by Stripe.
            </Text>
          </View>

          {/* Payment Methods Section */}
          {!isLoadingPaymentMethods && (
            <View style={styles.paymentMethodsSection}>
              <Text style={styles.sectionTitle}>Payment Method</Text>

              {paymentMethods.length > 0 &&
                paymentMethods.map((pm) => {
                  // Debug logging
                  if (pm.is_default) {
                    console.log('[CheckoutScreen] Rendering default payment method:', {
                      id: pm.stripe_payment_method_id,
                      is_default: pm.is_default,
                      last4: pm.card_last4,
                      brand: pm.card_brand
                    });
                  }
                  return (
                  <TouchableOpacity
                    key={pm.stripe_payment_method_id}
                    style={[
                      styles.savedPaymentMethod,
                      selectedPaymentMethodId === pm.stripe_payment_method_id &&
                      !useNewCard
                        ? styles.selectedPaymentMethod
                        : {},
                    ]}
                    onPress={() => {
                      setSelectedPaymentMethodId(pm.stripe_payment_method_id);
                      setUseNewCard(false);

                      // Track payment method selection
                      try {
                        trackEvent(
                          "payment_method_selected",
                          {
                            method_type: "saved_card",
                            is_default_card: pm.is_default,
                            brand: pm.card_brand,
                          },
                          posthog || undefined
                        );
                      } catch (analyticsError) {
                        console.warn(
                          "[CheckoutScreen] Failed to track payment_method_selected (saved card):",
                          analyticsError
                        );
                      }
                    }}
                  >
                    <View style={styles.paymentMethodInfo}>
                      <View style={styles.cardIconAndBrand}>
                        <Feather
                          name="credit-card"
                          size={20}
                          color={colors.primary}
                        />
                        <Text style={styles.cardBrand}>
                          {(pm.card_brand || "Card").toUpperCase()}
                        </Text>
                        {pm.is_default && (
                          <View style={styles.defaultBadge}>
                            <Text style={styles.defaultBadgeText}>DEFAULT</Text>
                          </View>
                        )}
                      </View>
                      <Text style={styles.cardNumber}>
                        •••• {pm.card_last4}
                      </Text>
                      <Text style={styles.cardExpiry}>
                        Expires {String(pm.card_exp_month).padStart(2, "0")}/
                        {String(pm.card_exp_year).slice(-2)}
                      </Text>
                    </View>
                    {selectedPaymentMethodId === pm.stripe_payment_method_id &&
                      !useNewCard && (
                        <Feather
                          name="check-circle"
                          size={24}
                          color={colors.primary}
                        />
                      )}
                  </TouchableOpacity>
                  );
                })}

              {/* Use Stripe Payment Sheet Option */}
              <TouchableOpacity
                style={[
                  styles.savedPaymentMethod,
                  useNewCard ? styles.selectedPaymentMethod : {},
                ]}
                onPress={() => {
                  setUseNewCard(true);
                  setSelectedPaymentMethodId(null);

                  // Track payment method selection for new card / Payment Sheet
                  try {
                    trackEvent(
                      "payment_method_selected",
                      {
                        method_type: "payment_sheet",
                      },
                      posthog || undefined
                    );
                  } catch (analyticsError) {
                    console.warn(
                      "[CheckoutScreen] Failed to track payment_method_selected (new card):",
                      analyticsError
                    );
                  }
                }}
              >
                <View style={styles.paymentMethodInfo}>
                  <View style={styles.cardIconAndBrand}>
                    <Feather
                      name="credit-card"
                      size={20}
                      color={colors.primary}
                    />
                    <Text style={styles.cardBrand}>
                      New Payment Method
                    </Text>
                  </View>
                  <Text style={styles.addNewCardText}>
                    Add new card or use another payment method
                  </Text>
                </View>
                {useNewCard && (
                  <Feather
                    name="check-circle"
                    size={24}
                    color={colors.primary}
                  />
                )}
              </TouchableOpacity>
            </View>
          )}

        </View>


        <TouchableOpacity
          style={[styles.payButton, storeHoursBlocked && styles.payButtonDisabled]}
          onPress={initializePayment}
          disabled={loading || isCreatingDelivery || storeHoursBlocked}
        >
          <Text style={styles.payButtonText}>
            {isCreatingDelivery
              ? "Creating Delivery..."
              : loading
              ? "Processing Payment..."
              : selectedPaymentMethodId && !useNewCard
              ? `Pay ${formatPrice(frozenTotalsRef.current?.payableTotal ?? payableTotal)} with saved card`
              : useNewCard
              ? `Pay ${formatPrice(frozenTotalsRef.current?.payableTotal ?? payableTotal)} with new card`
              : `Pay ${formatPrice(frozenTotalsRef.current?.payableTotal ?? payableTotal)}`}
          </Text>
        </TouchableOpacity>

        {deliveryQuote && deliveryQuote.length > 0 && (
          <View style={styles.deliveryNote}>
            <Feather name="truck" size={16} color={colors.primary} />
            <Text style={styles.deliveryNoteText}>
              Delivery will be created immediately after successful payment
            </Text>
          </View>
        )}
      </ScrollView>
    </SafeAreaView>
  );
}

const createStyles = (colors: ThemeColors) =>
  StyleSheet.create({
    safeArea: { flex: 1, backgroundColor: colors.background },
    container: { flex: 1, backgroundColor: colors.background },
    section: {
      backgroundColor: colors.background,
      margin: spacing.md,
      padding: spacing.md,
      borderRadius: 8,
      borderWidth: 1,
      borderColor: colors.border,
    },
    title: {
      fontSize: 20,
      fontWeight: "600",
      marginBottom: spacing.md,
      color: colors.text,
    },
    cartItem: {
      flexDirection: "row",
      padding: spacing.sm,
    },
    itemImage: {
      width: 80,
      height: 80,
      borderRadius: 8,
      marginRight: spacing.md,
    },
    placeholderImage: { backgroundColor: colors.surface },
    itemDetails: { flex: 1, justifyContent: "space-between" },
    itemName: {
      fontSize: 16,
      fontWeight: "500",
      color: colors.text,
      marginBottom: 4,
    },
    itemQuantity: { fontSize: 14, color: colors.textLight, marginBottom: 4 },
    itemPrice: { fontSize: 16, fontWeight: "600", color: colors.primary },
    deliveryInfo: {
      marginTop: spacing.md,
      paddingTop: spacing.md,
      borderTopWidth: 1,
      borderTopColor: colors.border,
    },
    deliveryTitle: {
      fontSize: 18,
      fontWeight: "600",
      marginBottom: spacing.sm,
      color: colors.text,
    },
    deliveryItem: {
      padding: spacing.sm,
      backgroundColor: colors.backgroundSecondary,
      borderRadius: 6,
      marginBottom: spacing.sm,
    },
    vendorName: {
      fontSize: 16,
      fontWeight: "500",
      marginBottom: 4,
      color: colors.text,
    },
    pickupAddress: { fontSize: 14, color: colors.textLight, marginBottom: 2 },
    deliveryTime: { fontSize: 14, color: colors.textLight, marginBottom: 2 },
    deliveryFee: { fontSize: 14, fontWeight: "500", color: colors.primary },
    totalContainer: {
      marginTop: spacing.lg,
      paddingTop: spacing.md,
      borderTopWidth: 1,
      borderTopColor: colors.border,
    },
    summaryRow: {
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "center",
      paddingVertical: spacing.sm,
    },
    summaryLabel: { fontSize: 16, color: colors.text },
    summaryValue: { fontSize: 16, fontWeight: "500", color: colors.text },
    summarySubLabel: {
      fontSize: 14,
      color: colors.textLight,
      fontStyle: "italic",
      paddingLeft: spacing.md,
    },
    totalRow: { paddingTop: spacing.md },
    totalLabel: { fontSize: 18, fontWeight: "600", color: colors.text },
    totalAmount: { fontSize: 20, fontWeight: "600", color: colors.primary },
    infoContainer: {
      backgroundColor: colors.backgroundSecondary,
      marginHorizontal: spacing.md,
      padding: spacing.md,
      borderRadius: 8,
    },
    infoBox: {
      flexDirection: "row",
      alignItems: "center",
      marginBottom: spacing.sm,
    },
    infoText: { flex: 1, marginLeft: spacing.sm, color: colors.text },
    payButton: {
      backgroundColor: colors.primary,
      padding: spacing.md,
      borderRadius: 8,
      alignItems: "center",
      marginHorizontal: spacing.md,
      marginTop: spacing.sm,
    },
    payButtonDisabled: {
      backgroundColor: colors.border,
      opacity: 0.6,
    },
    payButtonText: { color: colors.surface, fontWeight: "600", fontSize: 16 },
    deliveryNote: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: colors.backgroundSecondary,
      marginHorizontal: spacing.md,
      marginTop: spacing.sm,
      padding: spacing.sm,
      borderRadius: 6,
      gap: spacing.xs,
    },
    deliveryNoteText: {
      color: colors.primary,
      fontSize: 13,
      fontWeight: "500",
      textAlign: "center",
      flex: 1,
    },
    emptyCartText: {
      fontSize: 16,
      color: colors.textLight,
      textAlign: "center",
      padding: spacing.lg,
      fontStyle: "italic",
    },
    // Payment Methods Styles
    paymentMethodsSection: {
      backgroundColor: colors.background,
      marginVertical: spacing.md,
      padding: spacing.md,
      borderRadius: 8,
      borderWidth: 1,
      borderColor: colors.border,
    },
    sectionTitle: {
      fontSize: 18,
      fontWeight: "600",
      marginBottom: spacing.md,
      color: colors.text,
    },
    savedPaymentMethod: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      backgroundColor: colors.background,
      padding: spacing.md,
      marginBottom: spacing.sm,
      borderRadius: 8,
      borderWidth: 1,
      borderColor: colors.border,
    },
    selectedPaymentMethod: {
      borderColor: colors.primary,
      borderWidth: 2,
      backgroundColor: colors.backgroundSecondary,
    },
    paymentMethodInfo: {
      flex: 1,
    },
    cardIconAndBrand: {
      flexDirection: "row",
      alignItems: "center",
      marginBottom: spacing.xs,
    },
    cardBrand: {
      fontSize: 14,
      fontWeight: "600",
      color: colors.text,
      marginLeft: spacing.xs,
    },
    defaultBadge: {
      marginLeft: spacing.xs,
      paddingHorizontal: 6,
      paddingVertical: 3,
      backgroundColor: colors.primary,
      borderRadius: 4,
      minWidth: 50,
    },
    defaultBadgeText: {
      fontSize: 11,
      fontWeight: "700",
      color: colors.surface,
      letterSpacing: 0.5,
    },
    cardNumber: {
      fontSize: 16,
      fontWeight: "500",
      color: colors.text,
      marginBottom: 2,
    },
    cardExpiry: {
      fontSize: 13,
      color: colors.textLight,
    },
    addNewCardText: {
      fontSize: 13,
      color: colors.textLight,
      marginTop: 2,
    },
  });
